// ===========================================
// Messages Page
// ===========================================
// Main messaging interface for all user roles
// Two-column layout: conversations list + message thread
// Supports fun mode styling for students

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { messagesBasePath } from './messagesBasePath';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  Container,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/useAuth';
import { useTenant } from '../../contexts/TenantContext';
import { useStudentLayout } from '../../contexts/StudentLayoutContext';
import {
  useConversations,
  useConversationMessages,
  useSendMessage,
  useMarkAsRead,
  useRecipients,
} from '../../hooks/useMessages';
import {
  ConversationList,
  MessageThread,
  ComposeMessage,
  NewConversationModal,
} from '../../components/messages';
import { useMyStudentProfile } from '../../hooks/useStudentProfile';
import {
  getAgeGroupFromBirthDate,
  getCharacterColor,
  getCharacterName,
  AgeGroup,
} from '../../components/brand';
import {
  StudentAvatar,
  LayoutToggle,
  FunHeader,
  FunDecorations,
  FunUserMenu,
} from '../../components/student';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function MessagesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { user, logout } = useAuth();
  const { layoutMode } = useStudentLayout();
  const { schoolSlug } = useTenant();

  // Check if student in fun mode
  const isStudent = user?.role === 'STUDENT';
  const isFunMode = isStudent && layoutMode === 'fun';

  // Student profile for fun mode header
  const { data: studentProfile } = useMyStudentProfile({ enabled: isStudent });

  // Calculate character color for fun mode
  const ageGroup: AgeGroup = studentProfile?.birthDate
    ? getAgeGroupFromBirthDate(studentProfile.birthDate)
    : 'KIDS';
  const avatarCharacterMap: Record<string, AgeGroup> = {
    alice: 'PRESCHOOL',
    steve: 'KIDS',
    liam: 'TEENS',
    floyd: 'ADULT',
  };
  const avatarCharacter = studentProfile?.avatar?.split('/')[0] || '';
  const avatarAgeGroup = avatarCharacterMap[avatarCharacter] || ageGroup;
  const characterColor = getCharacterColor(avatarAgeGroup);
  const characterName = getCharacterName(avatarAgeGroup);

  // Brand colors for fun mode message panels
  const panelColors = {
    header: { border: '#FFAE9E', bg: 'rgba(255, 174, 158, 0.12)' },        // Coral
    conversations: { border: '#4580E4', bg: 'rgba(69, 128, 228, 0.08)' },  // Blue
    messages: { border: '#96DAC9', bg: 'rgba(150, 218, 201, 0.12)' },      // Mint
  };

  // State
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    conversationId || null
  );
  const [newMessageModalOpen, setNewMessageModalOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // User menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate(`/${schoolSlug}/login`);
  };

  // Queries
  const { data: conversations = [], isLoading: conversationsLoading } = useConversations();
  const {
    data: messagesData,
    isLoading: messagesLoading,
  } = useConversationMessages(selectedConversationId);
  const { data: recipients = [], isLoading: recipientsLoading } = useRecipients();

  // Mutations
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();

  // Get selected conversation details
  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  // Update URL when conversation changes
  useEffect(() => {
    if (selectedConversationId && conversationId !== selectedConversationId) {
      // The view the user is on, not their login role (roles 1b §5.15).
      const basePath = messagesBasePath(location.pathname, user?.role);
      navigate(`/${schoolSlug}${basePath}/${selectedConversationId}`, { replace: true });
    }
  }, [selectedConversationId, conversationId, navigate, user?.role, schoolSlug, location.pathname]);

  // Set selected conversation from URL param
  useEffect(() => {
    if (conversationId) {
      setSelectedConversationId(conversationId);
    }
  }, [conversationId]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversationId && selectedConversation?.unreadCount && selectedConversation.unreadCount > 0) {
      markAsRead.mutate(selectedConversationId);
    }
  }, [selectedConversationId, selectedConversation?.unreadCount]);

  // Handlers
  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleSendMessage = (content: string) => {
    if (!selectedConversationId || !selectedConversation) return;

    const recipientId =
      selectedConversation.participant1Id === user?.id
        ? selectedConversation.participant2Id
        : selectedConversation.participant1Id;

    sendMessage.mutate({ recipientId, content });
  };

  const handleNewMessage = (recipientId: string, content: string) => {
    sendMessage.mutate(
      { recipientId, content },
      {
        onSuccess: () => {
          // Find or create the conversation with this recipient
          // The conversation will be created by the backend if it doesn't exist
          // Refetch conversations to get the new one
          setNewMessageModalOpen(false);
        },
      }
    );
  };

  // Conversation list component (used in both desktop sidebar and mobile drawer)
  const conversationListContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Messages</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setNewMessageModalOpen(true)}
        >
          New
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <ConversationList
          conversations={conversations}
          selectedId={selectedConversationId}
          loading={conversationsLoading}
          onSelect={handleSelectConversation}
        />
      </Box>
    </Box>
  );

  // Message thread content
  const messageThreadContent = selectedConversationId ? (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isMobile && (
          <IconButton onClick={() => setMobileDrawerOpen(true)} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6">
          {selectedConversation
            ? `${selectedConversation.otherParticipant.firstName} ${selectedConversation.otherParticipant.lastName}`
            : 'Loading...'}
        </Typography>
      </Box>

      {/* Messages */}
      <MessageThread
        messages={messagesData?.messages || []}
        currentUserId={user?.id || ''}
        loading={messagesLoading}
        otherParticipantName={
          selectedConversation
            ? `${selectedConversation.otherParticipant.firstName}`
            : undefined
        }
      />

      {/* Compose */}
      <ComposeMessage
        onSend={handleSendMessage}
        loading={sendMessage.isPending}
        placeholder={
          selectedConversation
            ? `Message ${selectedConversation.otherParticipant.firstName}...`
            : 'Type a message...'
        }
      />
    </Box>
  ) : (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Select a conversation
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
        Choose a conversation from the list or start a new message.
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setNewMessageModalOpen(true)}
      >
        New Message
      </Button>
    </Box>
  );

  // Standard messages content
  const messagesContent = (
    <Box sx={{ height: isFunMode ? 'calc(100vh - 180px)' : 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Desktop layout */}
      {!isMobile && (
        <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Conversations sidebar */}
          <Grid item xs={4} sx={{ height: '100%', borderRight: 1, borderColor: 'divider' }}>
            <Paper
              sx={{
                height: '100%',
                borderRadius: isFunMode ? 4 : 0,
                ...(isFunMode && {
                  border: `3px solid ${panelColors.conversations.border}`,
                  bgcolor: panelColors.conversations.bg,
                  boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
                }),
              }}
              elevation={0}
            >
              {conversationListContent}
            </Paper>
          </Grid>

          {/* Message thread */}
          <Grid item xs={8} sx={{ height: '100%' }}>
            <Paper
              sx={{
                height: '100%',
                borderRadius: isFunMode ? 4 : 0,
                ...(isFunMode && {
                  border: `3px solid ${panelColors.messages.border}`,
                  bgcolor: panelColors.messages.bg,
                  boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
                  ml: 1,
                }),
              }}
              elevation={0}
            >
              {messageThreadContent}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Mobile layout */}
      {isMobile && (
        <>
          <Paper
            sx={{
              flex: 1,
              overflow: 'hidden',
              ...(isFunMode && {
                borderRadius: 4,
                border: `3px solid ${selectedConversationId ? panelColors.messages.border : panelColors.conversations.border}`,
                bgcolor: selectedConversationId ? panelColors.messages.bg : panelColors.conversations.bg,
                boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
              }),
            }}
            elevation={0}
          >
            {selectedConversationId ? (
              messageThreadContent
            ) : (
              conversationListContent
            )}
          </Paper>

          {/* Mobile drawer for conversation list */}
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            PaperProps={{
              sx: { width: '85%', maxWidth: 360 },
            }}
          >
            {conversationListContent}
          </Drawer>
        </>
      )}

      {/* New message modal */}
      <NewConversationModal
        open={newMessageModalOpen}
        recipients={recipients}
        loading={sendMessage.isPending}
        recipientsLoading={recipientsLoading}
        onClose={() => setNewMessageModalOpen(false)}
        onSend={handleNewMessage}
      />
    </Box>
  );

  // Fun mode wrapper for students
  if (isFunMode && studentProfile) {
    // Attendance rate from profile
    const attendanceRate = studentProfile?.attendanceRate ?? null;

    // User menu button for the header
    const userMenuButton = (
      <FunUserMenu
        studentProfile={studentProfile}
        characterColor={characterColor}
        anchorEl={userMenuAnchor}
        onOpen={handleUserMenuOpen}
        onClose={handleUserMenuClose}
        onLogout={handleLogout}
        onNavigate={navigate}
      />
    );

    return (
      <Box
        sx={{
          bgcolor: '#FCF6E6',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 1,
        }}
      >
        {/* Fun mode decorations */}
        <FunDecorations characterName={characterName} />

        {/* Fun Header (Piano) with user menu */}
        <FunHeader
          firstName={studentProfile.firstName}
          characterColor={characterColor}
          userMenuButton={userMenuButton}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 4 }}>
          {/* Student Header Bar - matches dashboard exactly */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: panelColors.header.bg,
              borderRadius: 4,
              border: `3px solid ${panelColors.header.border}`,
              boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {/* Avatar - 80x80 with 3px border, 70px avatar */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3,
                flexShrink: 0,
                border: `3px solid ${panelColors.header.border}`,
              }}
            >
              <StudentAvatar
                avatar={studentProfile.avatar}
                birthDate={studentProfile.birthDate}
                firstName={studentProfile.firstName}
                lastName={studentProfile.lastName}
                size={70}
              />
            </Box>

            {/* Name and Info */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="text.primary"
                sx={{
                  fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                  color: characterColor,
                }}
              >
                {studentProfile.firstName} {studentProfile.lastName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <SchoolIcon fontSize="small" sx={{ color: characterColor }} />
                <Typography variant="body2" color="text.secondary">
                  {studentProfile.enrollmentCount > 0
                    ? `${studentProfile.enrollmentCount} ${studentProfile.enrollmentCount === 1 ? 'class' : 'classes'} enrolled`
                    : 'No active enrollments'}
                </Typography>
                {attendanceRate !== null && (
                  <>
                    <Typography variant="body2" color="text.disabled">|</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(attendanceRate)}% attendance
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Navigation and Layout Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Dashboard Button */}
              <Button
                variant={location.pathname === `/${schoolSlug}/student` ? 'contained' : 'outlined'}
                size="small"
                startIcon={<DashboardIcon />}
                onClick={() => navigate(`/${schoolSlug}/student`)}
                sx={{
                  borderColor: characterColor,
                  color: location.pathname === `/${schoolSlug}/student` ? 'white' : characterColor,
                  bgcolor: location.pathname === `/${schoolSlug}/student` ? characterColor : 'transparent',
                  '&:hover': {
                    borderColor: characterColor,
                    bgcolor: location.pathname === `/${schoolSlug}/student` ? characterColor : `${characterColor}20`,
                  },
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Dashboard
              </Button>

              {/* Messages Button */}
              <Button
                variant={location.pathname.startsWith(`/${schoolSlug}/student/messages`) ? 'contained' : 'outlined'}
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => navigate(`/${schoolSlug}/student/messages`)}
                sx={{
                  borderColor: characterColor,
                  color: location.pathname.startsWith(`/${schoolSlug}/student/messages`) ? 'white' : characterColor,
                  bgcolor: location.pathname.startsWith(`/${schoolSlug}/student/messages`) ? characterColor : 'transparent',
                  '&:hover': {
                    borderColor: characterColor,
                    bgcolor: location.pathname.startsWith(`/${schoolSlug}/student/messages`) ? characterColor : `${characterColor}20`,
                  },
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Messages
              </Button>

              {/* Divider */}
              <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: `${characterColor}40` }} />

              {/* Layout Toggle */}
              <LayoutToggle showLabel={false} />
            </Box>
          </Paper>

          {/* Messages Content */}
          {messagesContent}
        </Container>
      </Box>
    );
  }

  // Standard layout for non-students or classic mode
  return messagesContent;
}
